import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { dentalProposalData } from '../data/dentalProposalMock';
import { 
  Phone, Globe, MessageSquare, CheckCircle2, ArrowRight, 
  Check
} from 'lucide-react';

export const DentalProposal = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Dual Branding Header */}
      <header className="bg-white py-6 border-b border-gray-200">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="flex items-center">
            {/* ClyniCall Logo Only - Top Left */}
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">ClyniCall</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 md:py-20">
        <div className="container mx-auto px-6 max-w-6xl">
          {/* Large, Prominent Platinum Dental Logo - Hero Focal Point */}
          <div className="flex justify-center mb-20">
            <div className="bg-white p-8 rounded-2xl shadow-2xl">
              <img 
                src={dentalProposalData.logos.platinumDental} 
                alt="Platinum Dental Care" 
                className="w-full max-w-md md:max-w-lg h-auto"
                style={{ minHeight: '120px' }}
              />
            </div>
          </div>
          
          <div className="text-center mt-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
              {dentalProposalData.hero.headline}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 leading-relaxed max-w-4xl mx-auto">
              {dentalProposalData.hero.subheadline}
            </p>
          </div>
        </div>
      </section>

      {/* Core Problem Section - Redesigned */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-16 text-center">
            Core Issues in Your Current Revenue Flow
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            {/* LEFT SIDE: Structured Issue Breakdown */}
            <div className="space-y-8">
              {/* Demand Capture */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-slate-200 pb-2">
                  Demand Capture
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Empty slots despite available clinical capacity</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Missed or delayed responses to inbound enquiries</span>
                  </li>
                </ul>
              </div>
              
              {/* Conversion Efficiency */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-slate-200 pb-2">
                  Conversion Efficiency
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Enquiries not systematically followed up</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                    <span>No structured booking conversion workflow</span>
                  </li>
                </ul>
              </div>
              
              {/* Utilisation */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-slate-200 pb-2">
                  Utilisation
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Reactive diary management</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700">
                    <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Underutilised chair time across the week</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* RIGHT SIDE: Visual Diagnostic Element */}
            <div className="flex items-center justify-center">
              <Card className="border-2 border-slate-200 bg-slate-50 w-full">
                <CardContent className="p-8">
                  <div className="space-y-6">
                    <div className="text-center">
                      <div className="bg-green-100 text-green-800 px-6 py-4 rounded-lg font-semibold text-lg mb-4">
                        Inbound Demand
                      </div>
                      <ArrowRight className="w-8 h-8 text-slate-400 mx-auto rotate-90 my-2" />
                      
                      <div className="bg-orange-100 text-orange-800 px-6 py-4 rounded-lg font-semibold text-lg mb-4">
                        Slow Response
                      </div>
                      <ArrowRight className="w-8 h-8 text-slate-400 mx-auto rotate-90 my-2" />
                      
                      <div className="bg-red-100 text-red-800 px-6 py-4 rounded-lg font-semibold text-lg mb-4">
                        Lost / Cold Leads
                      </div>
                      <ArrowRight className="w-8 h-8 text-slate-400 mx-auto rotate-90 my-2" />
                      
                      <div className="bg-slate-900 text-white px-6 py-4 rounded-lg font-semibold text-lg">
                        Empty Slots
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <p className="text-2xl font-semibold text-center text-gray-900 mt-12">
            {dentalProposalData.closingLine}
          </p>
        </div>
      </section>

      {/* The Imperative */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center">
            <blockquote className="text-2xl md:text-3xl font-bold mb-8 leading-relaxed">
              {dentalProposalData.imperative.quote}
            </blockquote>
            <p className="text-xl font-medium">
              {dentalProposalData.imperative.tagline}
              <span className="underline decoration-2">{dentalProposalData.imperative.taglineUnderline}</span>.
            </p>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-amber-600 to-yellow-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              THE SOLUTION
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {dentalProposalData.solution.title}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              {dentalProposalData.solution.description}
            </p>
          </div>
        </div>
      </section>

      {/* 3 Core Components */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            The 3 Core Components
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {dentalProposalData.components.map((component, index) => {
              const icons = [Phone, Globe, MessageSquare];
              const Icon = icons[index];
              return (
                <Card key={index} className="border-2 border-slate-200 hover:border-amber-400 hover:shadow-xl transition-all bg-white">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-amber-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">{component.title}</h3>
                    <ul className="space-y-3">
                      {component.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-700">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <div className="bg-amber-100 border-l-4 border-amber-600 p-6 rounded-r-lg">
            <p className="text-gray-900 font-medium flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-amber-600 flex-shrink-0" />
              {dentalProposalData.systemNote}
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            How It Works
          </h2>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12">
            {dentalProposalData.howItWorks.map((step, index) => (
              <React.Fragment key={index}>
                <div className="flex-1 text-center">
                  <div className="w-16 h-16 bg-slate-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-bold text-gray-900">{step.title}</h3>
                </div>
                {index < dentalProposalData.howItWorks.length - 1 && (
                  <ArrowRight className="hidden md:block w-8 h-8 text-slate-400 flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
          
          <p className="text-center text-xl text-gray-700 font-medium">
            {dentalProposalData.workflowNote}
          </p>
        </div>
      </section>

      {/* 30-Day Transformation (Expanded) */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center">
            30-Day Transformation
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {dentalProposalData.transformation.phases.map((phase, index) => {
              const colors = ['border-blue-500', 'border-amber-500', 'border-orange-500', 'border-green-500'];
              return (
                <Card key={index} className={`bg-slate-800 border-2 ${colors[index]}`}>
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold mb-6 text-white">{phase.title}</h3>
                    <ul className="space-y-3">
                      {phase.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-slate-300">
                          <Check className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            The Value
          </h2>
          
          <div className="bg-slate-50 border-2 border-slate-200 rounded-xl overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="text-left p-6 text-lg font-bold">Scenario</th>
                  <th className="text-left p-6 text-lg font-bold">Monthly Impact</th>
                </tr>
              </thead>
              <tbody>
                {dentalProposalData.value.scenarios.map((scenario, index) => (
                  <tr key={index} className="border-t border-slate-200">
                    <td className="p-6 font-semibold text-gray-900">{scenario.scenario}</td>
                    <td className="p-6 font-bold text-green-600 text-xl">{scenario.monthlyImpact}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="bg-amber-50 border-l-4 border-amber-600 p-8 rounded-r-xl">
            <p className="text-xl text-gray-900 font-semibold">
              {dentalProposalData.value.note}
            </p>
          </div>
        </div>
      </section>

      {/* Why This Wins */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-16 text-center">
            Why This Wins
          </h2>
          
          <div className="space-y-8">
            {dentalProposalData.comparison.map((item, index) => (
              <Card key={index} className="border-2 border-slate-200 bg-white">
                <CardContent className="p-8 md:p-12">
                  <div className="grid md:grid-cols-3 gap-8 items-center">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.competitor}</h3>
                      <ul className="space-y-2">
                        {item.theirApproach.map((approach, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-600">
                            <span className="text-red-500 font-bold">✗</span>
                            <span>{approach}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="flex justify-center">
                      <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-xl">VS</span>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-2xl font-bold text-amber-600 mb-4">The ClyniCall system</h3>
                      <ul className="space-y-2">
                        {item.ourApproach.map((approach, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-900 font-semibold">
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span>{approach}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {dentalProposalData.pricing.title}
          </h2>
          
          <Card className="border-2 border-slate-300 bg-slate-50">
            <CardContent className="p-12">
              <div className="text-center mb-8">
                <div className="text-5xl font-bold text-gray-900 mb-4">
                  {dentalProposalData.pricing.monthly}
                </div>
                <div className="text-2xl font-semibold text-gray-700">
                  {dentalProposalData.pricing.implementation}
                </div>
              </div>
              
              <div className="border-t-2 border-slate-300 pt-8 mb-8">
                <p className="text-lg text-gray-900 leading-relaxed mb-6">
                  {dentalProposalData.pricing.positioning}
                </p>
                <p className="text-lg text-gray-700 font-semibold">
                  {dentalProposalData.pricing.roiFraming}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="bg-slate-950 text-slate-500 py-8">
        <div className="container mx-auto px-6 text-center">
          <p className="text-sm">© 2024 ClyniCall. Confidential Proposal.</p>
        </div>
      </footer>
    </div>
  );
};

export default DentalProposal;
